/** @graphql cc **/
import type { MouseEvent, KeyboardEvent } from 'react';
import React from 'react';
import { useIntl, FormattedMessage, defineMessages } from 'react-intl-next';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { differenceInDays } from 'date-fns';

import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import AutomationIcon from '@atlaskit/icon/core/automation';
import EditBulkIcon from '@atlaskit/icon/core/edit-bulk';
import ChartTrendIcon from '@atlaskit/icon/core/chart-trend';
import { LinkItem, Section } from '@atlaskit/menu';
import Lozenge from '@atlaskit/lozenge';
import { Inline } from '@atlaskit/primitives';

import { ConfluenceEdition } from '@confluence/session-data';
import {
	SPACE_PERMISSIONS_SETTINGS,
	SPACE_SETTINGS_AUTOMATION,
	SPACE_BULK_TOOLS,
	SPACE_ANALYTICS,
} from '@confluence/named-routes';
import { SpacePermissionsTabKey } from '@confluence/space-permissions/entry-points/spacePermissionsKeys';
import { isGraphQLError } from '@confluence/network';
import { isErrorMarkedAsHandled, markErrorAsHandled } from '@confluence/graphql';

import type { SpaceMoreActionsMenuSpaceToolsQuery as SpaceMoreActionsMenuSpaceToolsQuery$data } from './__types__/SpaceMoreActionsMenuSpaceToolsQuery.ts';

type SpaceMoreActionsMenuSpaceToolsProps = {
	isSpaceAdmin: boolean;
	edition: ConfluenceEdition | null;
	spaceKey: string;
	onMenuItemClicked: (
		selection: string,
	) => (e: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => void;
};

export const SpaceMoreActionsMenuSpaceTools = ({
	isSpaceAdmin,
	edition,
	spaceKey,
	onMenuItemClicked,
}: SpaceMoreActionsMenuSpaceToolsProps) => {
	const intl = useIntl();
	const showSpaceTools = isSpaceAdmin && edition === ConfluenceEdition.PREMIUM;

	const { data: trialData, error: trialDetailsError } =
		useQuery<SpaceMoreActionsMenuSpaceToolsQuery$data>(
			gql`
				query SpaceMoreActionsMenuSpaceToolsQuery {
					license {
						trialEndDate
					}
				}
			`,
			{
				skip: !showSpaceTools,
			},
		);

	if (isGraphQLError(trialDetailsError) && !isErrorMarkedAsHandled(trialDetailsError)) {
		markErrorAsHandled(trialDetailsError);
	}

	const isPremiumTrial = getTrialDaysLeft(trialData?.license?.trialEndDate) > 0;

	return (
		showSpaceTools && (
			<Section title={intl.formatMessage(i18n.spaceTools)} hasSeparator>
				<LinkItem
					href={SPACE_PERMISSIONS_SETTINGS.toUrl({
						spaceKey,
						tab: SpacePermissionsTabKey.Users,
					})}
					iconBefore={<PeopleGroupIcon label="" color="currentColor" />}
					onClick={onMenuItemClicked('spaceAddUsers')}
					UNSAFE_shouldDisableRouterLink
				>
					<FormattedMessage {...i18n.addUsers} />
				</LinkItem>
				<LinkItem
					href={SPACE_SETTINGS_AUTOMATION.toUrl({ spaceKey })}
					iconBefore={<AutomationIcon label="" color="currentColor" />}
					onClick={onMenuItemClicked('spaceAutomation')}
					UNSAFE_shouldDisableRouterLink
				>
					<Inline space="space.150" alignBlock="center">
						<FormattedMessage {...i18n.automation} />
						{isPremiumTrial && (
							<Lozenge appearance="new">
								<FormattedMessage {...i18n.premiumLozenge} />
							</Lozenge>
						)}
					</Inline>
				</LinkItem>
				<LinkItem
					href={SPACE_BULK_TOOLS.toUrl({ spaceKey })}
					iconBefore={<EditBulkIcon label="" color="currentColor" />}
					onClick={onMenuItemClicked('spaceContentManager')}
					UNSAFE_shouldDisableRouterLink
				>
					<Inline space="space.150" alignBlock="center">
						<FormattedMessage {...i18n.contentManager} />
						{isPremiumTrial && (
							<Lozenge appearance="new">
								<FormattedMessage {...i18n.premiumLozenge} />
							</Lozenge>
						)}
					</Inline>
				</LinkItem>
				<LinkItem
					href={SPACE_ANALYTICS.toUrl({ spaceKey })}
					iconBefore={<ChartTrendIcon label="" color="currentColor" />}
					onClick={onMenuItemClicked('spaceAnalytics')}
					UNSAFE_shouldDisableRouterLink
				>
					<Inline space="space.150" alignBlock="center">
						<FormattedMessage {...i18n.analytics} />
						{isPremiumTrial && (
							<Lozenge appearance="new">
								<FormattedMessage {...i18n.premiumLozenge} />
							</Lozenge>
						)}
					</Inline>
				</LinkItem>
			</Section>
		)
	);
};

// This is copied from next/packages/premium-onboarding/src/useTrialDetails.tsx
const getTrialDaysLeft = (trialEndDate: string | null | undefined) => {
	if (!trialEndDate) {
		return 0;
	}

	// +1 day to round up for fractional days
	return differenceInDays(new Date(trialEndDate), new Date()) + 1;
};

const i18n = defineMessages({
	spaceTools: {
		id: 'side-navigation.space-navigation.space-tools',
		defaultMessage: 'Space tools',
		description: 'Section header within the more actions menu for Space tools.',
	},
	addUsers: {
		id: 'side-navigation.space-navigation.add-users',
		defaultMessage: 'Add users',
		description:
			'Menu item to navigate to the space permissions tab in space settings that allows you to add users to a space. Appears in the space more actions menu.',
	},
	automation: {
		id: 'side-navigation.space-navigation.automation',
		defaultMessage: 'Automation',
		description:
			'Menu item to navigate to the automation page in space settings. Appears in the space more actions menu.',
	},
	contentManager: {
		id: 'side-navigation.space-navigation.content-manager',
		defaultMessage: 'Content manager',
		description:
			'Menu item to navigate to the content manager page in space settings. Appears in the space more actions menu.',
	},
	analytics: {
		id: 'side-navigation.space-navigation.analytics',
		defaultMessage: 'Analytics',
		description:
			'Menu item to navigate to the analytics page. Appears in the space more actions menu.',
	},
	premiumLozenge: {
		id: 'side-navigation.space-navigation.premium-lozenge',
		defaultMessage: 'Premium',
		description:
			'Lozenge text for the premium edition of Confluence. Appears in the space more actions menu to label premium space tools.',
	},
});
